import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18n from '~/i18n';
import { DeleteOutlined, CheckCircleOutlineOutlined } from '@material-ui/icons';
import { TextField, Typography, useTheme } from '@material-ui/core';
import PF2MSelectOption from '~/components/PF2MSelectOption';
import PF2MGenericButton from '~/components/PF2MGenericButton';
import PF2MMaskTextField from '~/components/PF2MMaskTextField';
import PF2MDatepickerNew from '~/components/PF2MDatepickerNew';
import { parseDateTime } from '~/utils/moment';
import { updateEquipmentsSchedules } from '~/store/dispatch/actions';
import { SCHEDULE_OBSERVATION_CHARACTER_LIMIT } from '../constants';
import {
  applyFieldCascade,
  buildElementsOptions,
  buildSubElementsOptions,
  buildCodesOptions,
  getSelectedCodeOptionValue,
  formatDateTime,
  getDateErrorMessage,
  hasFormChanged,
  isFormValid,
} from './utils';

const ScheduleTab = ({
  classes,
  currentModalStates,
  setScheduleState,
  lookupCtx,
  onClose,
}) => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const format = i18n.t('date:DateFormat');
  const equipmentScheduleStatuses = useSelector(state => state.dispatch.equipmentScheduleStatuses);

  const {
    form = {},
    originalForm = {},
    dateError = null,
  } = currentModalStates || {};

  const {
    codes,
    codesByKey,
    elementsById,
    subElementsById,
  } = lookupCtx || {};

  const pendingStatus = useMemo(() => equipmentScheduleStatuses.find(
    r => ['Pendente', 'Pending', 'Pendiente'].includes(r.name),
  ), [equipmentScheduleStatuses]);

  const defaultOption = useMemo(
    () => ({ value: 0, label: translate('common:Select') }),
    [translate],
  );

  const elementsOptions = useMemo(
    () => [defaultOption, ...buildElementsOptions(elementsById)],
    [defaultOption, elementsById],
  );

  const subElementsOptions = useMemo(
    () => [defaultOption, ...buildSubElementsOptions(
      subElementsById,
      form,
      'flowchart_element_id',
    )],
    [defaultOption, subElementsById, form],
  );

  const codesOptions = useMemo(
    () => [
      defaultOption,
      ...buildCodesOptions(
        codes,
        form,
      ),
    ],
    [defaultOption, codes, form],
  );

  const selectedCodeOptionValue = useMemo(
    () => getSelectedCodeOptionValue(codesByKey, form),
    [codesByKey, form],
  );

  const formIsValid = useMemo(
    () => isFormValid(dateError, form),
    [dateError, form],
  );

  const hasChanged = useMemo(
    () => hasFormChanged(form, originalForm),
    [form, originalForm],
  );

  const startDateTimeMoment = useMemo(() => {
    const startDateAndTimeStr = `${form?.start_date || ''} ${form?.start_time || ''}`;
    return form?.start_date ? parseDateTime(startDateAndTimeStr, true) : null;
  }, [form?.start_date, form?.start_time]);


  const handleFieldChange = field => (value) => {
    if (field === 'observation' && value?.length > SCHEDULE_OBSERVATION_CHARACTER_LIMIT) {
      return;
    }

    const nextForm = applyFieldCascade(form, field, value, lookupCtx);

    if (field === 'start_date') {
      const { start_date, start_time } = formatDateTime(value) || {};
      setScheduleState({
        form: nextForm,
        dateError: getDateErrorMessage(start_date, start_time, translate),
      });
      return;
    }

    setScheduleState({ form: nextForm });
  };

  const handleDiscard = () => {
    setScheduleState({
      form: originalForm,
      dateError: null,
    });
  };

  const handleSave = () => {
    if (!hasChanged || !formIsValid) {
      onClose();
      return;
    }

    const payload = {
      ...form,
      status_id: pendingStatus?.id,
      update_status: 'I',
      active: 1,
    };
    dispatch(updateEquipmentsSchedules([payload]));
    onClose();
  };

  return (
    <div className={classes.wrapperChildren}>
      <div className={classes.boxFields}>
        <div className={classes.wrapperDateTimePickerLabel}>
          <Typography className={classes.labelText}>{`${translate('common:DateAndTime')} *`}</Typography>
          <PF2MDatepickerNew
            isFormControlled={false}
            value={startDateTimeMoment}
            onChange={value => handleFieldChange('start_date')(value)}
            disableFuture={false}
            disablePast
            isDateTime
            format={`${format} HH:mm:ss`}
            views={['hours', 'minutes', 'seconds']}
            noParseDate
            data-testid="date-time-picker"
            error={!!dateError}
            helperText={dateError}
            style={{
              width: '100%',
              border: dateError ? '1px solid red' : `1px solid ${theme.palette.gray[20]}`,
              borderRadius: 4,
              padding: '6px 8px',
              height: '40px',
            }}
            inputProps={{
              style: {
                fontSize: '14px',
                lineHeight: '20px',
                color: theme.palette.gray[60],
              },
            }}
          />
        </div>
        <div className={classes.wrapperTextFieldLabel}>
          <Typography className={classes.labelText}>{`${translate('common:ProgrammingDuration')} *`}</Typography>
          <PF2MMaskTextField
            variant="outlined"
            mask="99:99:99"
            placeholder={'00:00:00'}
            value={form?.duration}
            onChange={e => handleFieldChange('duration')(e?.target?.value)}
            InputProps={{
              style: {
                height: 40,
                fontFamily: 'Roboto',
                fontWeight: 300,
                fontSize: 14,
                color: '#647886',
              },
            }}
          />
        </div>
        <PF2MSelectOption
          description={`${translate('common:Code')} *`}
          options={codesOptions}
          boxDescriptionClassName={classes.boxLabel}
          descriptionClassName={classes.labelText}
          onChange={e => handleFieldChange('codes_id')(e.target.value)}
          value={selectedCodeOptionValue}
        />
        <PF2MSelectOption
          description={`${translate('common:Element')} *`}
          options={elementsOptions}
          boxDescriptionClassName={classes.boxLabel}
          descriptionClassName={classes.labelText}
          onChange={e => handleFieldChange('flowchart_element_id')(e.target.value)}
          value={form?.flowchart_element_id}
        />
        <PF2MSelectOption
          description={`${translate('common:SubElement')} *`}
          options={subElementsOptions}
          boxDescriptionClassName={classes.boxLabel}
          descriptionClassName={classes.labelText}
          onChange={e => handleFieldChange('flowchart_element_point_id')(e.target.value)}
          value={form?.flowchart_element_point_id}
          disabled={!form?.flowchart_element_id || form?.flowchart_element_id === 0}
        />
      </div>
      <div className={classes.wrapperTextFieldLabel}>
        <Typography className={classes.labelText}>{translate('common:Observation')}</Typography>
        <TextField
          placeholder={translate('common:EnterText')}
          value={form?.observation}
          onChange={e => handleFieldChange('observation')(e.target.value)}
          variant="outlined"
          multiline
          minRows={3}
          maxRows={3}
          id="text-field"
        />
      </div>
      <div className={classes.wrapperActions}>
        <PF2MGenericButton
          className={classes.buttonActions}
          onClick={handleDiscard}
          startIcon={<DeleteOutlined fontSize="small" />}
        >
          {translate('common:DiscardChanges')}
        </PF2MGenericButton>
        <PF2MGenericButton
          variant={'contained'}
          color={'secondary'}
          className={classes.buttonActions}
          onClick={handleSave}
          startIcon={<CheckCircleOutlineOutlined fontSize="small" />}
          disabled={!formIsValid || !hasChanged}
        >
          {translate('common:Commit Changes')}
        </PF2MGenericButton>
      </div>
    </div>
  );
};

export default ScheduleTab;

ScheduleTab.propTypes = {
  classes: PropTypes.object.isRequired,
  currentModalStates: PropTypes.object.isRequired,
  setScheduleState: PropTypes.func.isRequired,
  lookupCtx: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};
